import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CtMetadata } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-metadata';
import { LayerItemV2 } from '@common-modules/wlm-map/generic-map/models/layer-item';
import {
  MapCssStyle,
  MapThematicStyle,
} from '@common-modules/wlm-map/generic-map/models/layer-type';
import { take } from 'rxjs';
import { ItemsDescription, MapLegendItems } from '../models/map-legend.items';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { MasterOperatorFields } from '@cloud-twin/master-operator/models/master-operator-fields.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { StateAreas } from '@common-modules/redux/models/state-areas';

const COMPONENT_SELECTOR = 'ct-map-legend-list';
@UntilDestroy()
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './map-legend-list.component.html',
  styleUrl: './map-legend-list.component.scss',
  standalone: false,
})
export class MapLegendListComponent implements OnInit
{
  private readonly _state = inject(ReduxStateService);

  @Input()
  set layers(value: LayerItemV2[]) {
    this._layers = value;
    if (value) {
      this.mapLegendItems = this.buildMapLegendItems(this.layers);
    }
  }
  get layers(): LayerItemV2[] {
    return this._layers;
  }

  private _popupVisibilityChange: boolean;

  @Input()
  set popupVisibilityChange(value: boolean) {
    this._popupVisibilityChange = value;
    this.isPopupVisible = value;
    if (!value) {
      this.isPopupVisible = false;
    }
  }

  get popupVisibilityChange(): boolean {
    return this._popupVisibilityChange;
  }

  @Output() popupStateChange = new EventEmitter<boolean>();
  constructor(){
    const stateWidgetSettings = new StateWidgetSettings({
      module: "Main",
      page: "MapPage",
      scopeInstanceKeys: {
        [StateAreas.Generic]: 'shared',
      },
    });

    this._state.configure(stateWidgetSettings);
  }

  private readonly _ctAttributeService = inject(CtAttributesService);

  private _layers: LayerItemV2[];
  mapLegendItems: MapLegendItems[];
  isPopupVisible: boolean = false;
  metadata: CtMetadata;

  ngOnInit(): void {
    this._ctAttributeService.metadata$.pipe(take(1)).subscribe((metadata) => {
      this.metadata = metadata;
    });
    this.listenThematicMapChanges();
  }

  showPopup(e) {
    e.stopPropagation();
    this.isPopupVisible = !this.isPopupVisible;
    this.popupStateChange.emit(this.isPopupVisible);
  }

  private buildMapLegendItems(layerItems: LayerItemV2[]): MapLegendItems[] {
    if (!layerItems) return [];
    return layerItems.map((layer) => {
      const legendItem = new MapLegendItems();
      legendItem.name = layer.layerStyle?.styleName
        ? `${layer.layerStyle.styleName}: ${layer.layerStyle?.fieldName || ''}`
        : '';
      legendItem.layerType = layer.layerDefinition?.layerType;
      legendItem.items = [];
      const styleDef = layer.layerStyle?.styleDefinition;
      let ranges: { value: number; styleDefinition: MapCssStyle }[] = [];
      if (styleDef && typeof styleDef !== 'string' && 'rangeDefinition' in styleDef) {
        ranges = (styleDef as MapThematicStyle).rangeDefinition;
      }
      legendItem.uom = this.buildUom(layer);
      if (Array.isArray(ranges) && ranges.length > 0) {
        for (let i = 0; i < ranges.length; i++) {
          const item = new ItemsDescription();
          const value = ranges[i].value;
          if (i === 0) {
            item.rangeDefinition = `Min - ${value}`;
          } else if (i === ranges.length - 1) {
            item.rangeDefinition = `${ranges[i - 1].value} - Max`;
          } else {
            item.rangeDefinition = `${ranges[i - 1].value} - ${value}`;
          }

          item.color = ranges[i].styleDefinition.definition.Color;
          item.iconPath = ranges[i].styleDefinition.definition.iconPath;
          legendItem.items.push(item);
        }
      }
      return legendItem;
    });
  }

  private buildUom(layerItem: LayerItemV2): string {
    const { objectType, fieldName } = layerItem.layerStyle;

    for (const scenarioId in this.metadata) {
      const categoriesArr = this.metadata[scenarioId];

      for (const cat of categoriesArr) {
        if (cat.type !== objectType || !cat.categories) continue;

        for (const category of cat.categories) {
          if (!category.attributes) continue;

          const attribute = category.attributes.find((attr) => attr.name === fieldName);
          if (attribute) {
            return attribute.units || '';
          }
        }
      }
    }

    return '';
  }

  private listenThematicMapChanges(): void {
    this._state
      .select<boolean>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.ThematicChanged,
        })
      )
      .pipe(untilDestroyed(this))
      .subscribe((value: boolean) => {
        if (value) {
          this.mapLegendItems = [...this.buildMapLegendItems(this.layers)]
        }
      });
  }
}
